import { NavLink, Outlet, useNavigate, Navigate, Link } from "react-router-dom";
import { LayoutDashboard, FileText, Globe2, UtensilsCrossed, BookOpen, Inbox, Wand2, LogOut, Rocket, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pages", label: "Pages & Sliders", icon: FileText },
  { to: "/admin/global", label: "Universal", icon: Globe2 },
  { to: "/admin/menus", label: "Menus", icon: UtensilsCrossed },
  { to: "/admin/blog", label: "Blog", icon: BookOpen },
  { to: "/admin/leads", label: "Requests", icon: Inbox },
  { to: "/admin/ai", label: "AI Updater", icon: Wand2 },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) return <div className="flex h-screen items-center justify-center bg-cast text-brass font-serif text-xl animate-pulse">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const publish = async () => {
    try {
      const { data } = await api.post("/admin/publish");
      toast.success(`Published to live — ${JSON.stringify(data.published)}`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex min-h-screen bg-cast" data-testid="admin-shell">
      <aside className="fixed inset-y-0 left-0 w-60 border-r border-brass/10 bg-charcoal p-6 flex flex-col">
        <p className="font-serif text-lg font-bold text-cream">Chef Blaine</p>
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brass mt-1">Site Manager</p>
        <nav className="mt-10 flex flex-col gap-1 flex-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-brass/10 text-brass" : "text-clay hover:text-cream hover:bg-cast"}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
        <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-button" className="flex items-center gap-3 px-3 py-2.5 text-sm text-clay hover:text-flame transition-colors">
          <LogOut size={16} /> Sign Out
        </button>
      </aside>
      <div className="ml-60 flex-1">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-brass/10 bg-cast/85 backdrop-blur-xl px-8 py-4">
          <p className="text-sm text-clay">Signed in as <span className="text-cream">{user.email}</span></p>
          <div className="flex items-center gap-3">
            <Link to="/staging" target="_blank" data-testid="admin-view-staging-button" className="flex items-center gap-2 rounded-full border border-brass/30 px-5 py-2 text-xs font-semibold uppercase tracking-widest text-brass transition-colors hover:bg-brass/10">
              <Eye size={14} /> Staging
            </Link>
            <button onClick={publish} data-testid="admin-publish-button" className="flex items-center gap-2 rounded-full bg-brass px-5 py-2 text-xs font-semibold uppercase tracking-widest text-cast transition-colors hover:bg-flame hover:text-cream">
              <Rocket size={14} /> Publish to Live
            </button>
          </div>
        </header>
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
